import React, { useCallback, useId, useMemo, useState } from 'react';
import { match, P } from 'ts-pattern';

import { CodeBlock } from '@fragment/ui/src/components/CodeBlock/CodeBlock';
import { InlineCode } from '@fragment/ui/src/components/InlineCode/InlineCode';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';
import { LoadingText } from '@fragment/ui/src/components/LoadingText';
import type { MenuItem } from '@fragment/ui/src/components/Menu/Menu';
import { Menu } from '@fragment/ui/src/components/Menu/Menu';
import { Table } from '@fragment/ui/src/components/Table';
import { TextInputCopy } from '@fragment/ui/src/components/TextInputCopy/TextInputCopy';

import type { McpClientNode } from '../../../mcpClient/types';
import { useGetWorkspaceMcpClientsQuery } from '../../../../hooks/internalApi';
import { useInternalApiContext } from '../../../../hooks/useInternalApiContext';
import { getResultOrError } from '../../../../utils/errors';
import {
  useWorkspace,
  useWorkspaceId,
} from '../../../../wrappers/WorkspaceProvider';
import { DeleteMcpClientForm } from '../../../mcpClient/DeleteMcpClientForm';
import { TableLoadError } from '../../../TableLoadError/TableLoadError';
import { getWorkspaceMcpServerName, getWorkspaceMcpUrl } from './mcpUrl';
import { SettingsModalLayout } from './SettingsModalLayout';

type McpSetupOption = 'claude' | 'cursor' | 'opencode' | 'codex' | 'url';

const MCP_SETUP_OPTION_LABELS: Record<McpSetupOption, string> = {
  claude: 'Claude',
  cursor: 'Cursor',
  opencode: 'Open Code',
  codex: 'Codex',
  url: 'URL',
};

const MCP_SETUP_OPTIONS: McpSetupOption[] = [
  'claude',
  'cursor',
  'opencode',
  'codex',
  'url',
];

const schema = [
  {
    title: 'Connection',
    key: 'name',
  },
  {
    title: 'ID',
    key: 'id',
    justify: 'right',
  },
] as const;

const emptyValue = {
  ID: 'N/A',
  Connection: 'No MCP clients yet',
};

const WorkspaceMcpUrl = () => {
  const fieldId = useId();
  const workspaceId = useWorkspaceId();
  const { workspace: activeWorkspaceFromContext } = useWorkspace();
  if (!activeWorkspaceFromContext?.pod) return null;
  return (
    <TextInputCopy
      id={fieldId}
      label="MCP Server URL"
      size="md"
      width="w-full"
      padding="p-0"
      border={false}
      value={getWorkspaceMcpUrl(
        activeWorkspaceFromContext.pod.customerApiUrl,
        workspaceId
      )}
    />
  );
};

const McpClientDetail = ({
  mcpClient,
  onConfirmedDelete,
}: {
  mcpClient: McpClientNode;
  onConfirmedDelete: VoidFunction;
}) => {
  const namespace = useId();

  return (
    <div className="space-y-f4">
      <div className="space-y-f2">
        <TextInput
          flexContainer="space-y-f0"
          id={`${namespace}-client-id`}
          label="Client ID"
          padding="p-0"
          value={mcpClient.id}
          size="md"
          width="w-full"
          readOnly
        />
        <WorkspaceMcpUrl />
      </div>
      <DeleteMcpClientForm mcpClient={mcpClient} onDelete={onConfirmedDelete} />
    </div>
  );
};

const Step = ({
  index,
  children,
}: {
  index: number;
  children: React.ReactNode;
}) => (
  <div className="flex items-start gap-x-[1ch]">
    <span className="text-main-500">{index}.</span>
    <span className="text-main">{children}</span>
  </div>
);

const Instructions = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-col w-full space-y-f2">{children}</div>
);

const ClaudeInstructions = ({
  serverName,
  mcpUrl,
}: {
  serverName: string;
  mcpUrl: string;
}) => (
  <Instructions>
    <Step index={1}>Paste the following in your terminal</Step>
    <CodeBlock language="bash">
      {`claude mcp add --transport http --scope user ${serverName} ${mcpUrl}`}
    </CodeBlock>
    <Step index={2}>
      Start Claude, run <InlineCode>/mcp</InlineCode> and complete the browser
      sign-in to authorize the connection.
    </Step>
  </Instructions>
);

const CursorInstructions = ({
  serverName,
  mcpUrl,
}: {
  serverName: string;
  mcpUrl: string;
}) => (
  <Instructions>
    <Step index={1}>
      Add the server to <InlineCode>~/.cursor/mcp.json</InlineCode> (global) or{' '}
      <InlineCode>.cursor/mcp.json</InlineCode> (project):
    </Step>
    <CodeBlock language="json">
      {`{
  "mcpServers": {
    "${serverName}": {
      "url": "${mcpUrl}"
    }
  }
}`}
    </CodeBlock>
    <Step index={2}>
      Open Customize → MCP in Cursor and authenticate when prompted.
    </Step>
  </Instructions>
);

const OpenCodeInstructions = ({
  serverName,
  mcpUrl,
}: {
  serverName: string;
  mcpUrl: string;
}) => (
  <Instructions>
    <Step index={1}>Add the server with the OpenCode CLI:</Step>
    <CodeBlock language="bash">
      {`opencode mcp add ${serverName} --url ${mcpUrl}`}
    </CodeBlock>
    <Step index={2}>Authenticate via OAuth:</Step>
    <CodeBlock language="bash">{`opencode mcp auth ${serverName}`}</CodeBlock>
  </Instructions>
);

const CodexInstructions = ({
  serverName,
  mcpUrl,
}: {
  serverName: string;
  mcpUrl: string;
}) => (
  <Instructions>
    <Step index={1}>
      Add the server to <InlineCode>~/.codex/config.toml</InlineCode>:
    </Step>
    <CodeBlock language="plaintext">
      {`[mcp_servers.${serverName}]
url = "${mcpUrl}"`}
    </CodeBlock>
    <Step index={2}>Complete the browser OAuth flow:</Step>
    <CodeBlock language="bash">{`codex mcp login ${serverName}`}</CodeBlock>
  </Instructions>
);

const UrlInstructions = () => (
  <Instructions>
    <WorkspaceMcpUrl />
    <p className="text-main">
      Paste this URL into your MCP host of choice. It will open the dashboard to
      authorize the connection.
    </p>
  </Instructions>
);

const McpSetupInstructions = ({ setup }: { setup: McpSetupOption }) => {
  const workspaceId = useWorkspaceId();
  const { workspace: activeWorkspaceFromContext } = useWorkspace();
  if (!activeWorkspaceFromContext?.pod) return null;
  const mcpUrl = getWorkspaceMcpUrl(
    activeWorkspaceFromContext.pod.customerApiUrl,
    workspaceId
  );
  const serverName = getWorkspaceMcpServerName(activeWorkspaceFromContext.name);

  return (
    <div className="space-y-f4">
      {match(setup)
        .with('claude', () => (
          <ClaudeInstructions serverName={serverName} mcpUrl={mcpUrl} />
        ))
        .with('cursor', () => (
          <CursorInstructions serverName={serverName} mcpUrl={mcpUrl} />
        ))
        .with('opencode', () => (
          <OpenCodeInstructions serverName={serverName} mcpUrl={mcpUrl} />
        ))
        .with('codex', () => (
          <CodexInstructions serverName={serverName} mcpUrl={mcpUrl} />
        ))
        .with('url', () => <UrlInstructions />)
        .exhaustive()}
    </div>
  );
};

export const McpClientsSettingsForm = ({
  hideHeader = false,
}: {
  hideHeader?: boolean;
} = {}) => {
  const workspaceId = useWorkspaceId();
  const { context } = useInternalApiContext();
  // Stable reference so urql doesn't re-issue the query on every render.
  const variables = useMemo(() => ({ workspaceId }), [workspaceId]);
  const [{ data, error, fetching }, refetchMcpClients] =
    useGetWorkspaceMcpClientsQuery({
      variables,
      context,
      pause: !context.token,
    });
  const { isError: isWorkspaceError, result: workspace } = getResultOrError(
    data?.workspace,
    error
  );
  const { isError: isMcpClientsError, result: mcpClients } = getResultOrError(
    workspace?.mcpClients,
    error
  );

  const [selectedClient, setSelectedClient] = useState<
    McpClientNode | undefined
  >(undefined);

  const [selectedSetup, setSelectedSetup] = useState<
    McpSetupOption | undefined
  >(undefined);

  const refresh = useCallback(
    () => refetchMcpClients({ requestPolicy: 'network-only' }),
    [refetchMcpClients]
  );

  const onRowClick = useCallback(
    (client: McpClientNode) => setSelectedClient(client),
    []
  );
  const isError = isWorkspaceError || isMcpClientsError;
  // No data yet and no error means the first response is still in flight (or
  // the query is paused awaiting a token) — a loading state, not an error.
  const isLoading = !data && !isError;

  const onCancel = useCallback(() => {
    setSelectedClient(undefined);
    setSelectedSetup(undefined);
  }, []);

  const onConfirmedDelete = useCallback(() => {
    setSelectedClient(undefined);
    refresh();
  }, [refresh]);

  const viewState: 'list' | 'error' | 'loading' | 'detail' | 'setup' = isError
    ? 'error'
    : match([selectedSetup, selectedClient, isLoading] as const)
        .with([P.not(undefined), P._, P._], () => 'setup' as const)
        .with([undefined, undefined, true], () => 'loading' as const)
        .with([undefined, P.not(undefined), P._], () => 'detail' as const)
        .otherwise(() => 'list' as const);

  const setupMenuItems: MenuItem[] = MCP_SETUP_OPTIONS.map((option) => ({
    key: option,
    display: MCP_SETUP_OPTION_LABELS[option],
    onClick: () => setSelectedSetup(option),
  }));

  return (
    <SettingsModalLayout
      hideHeader={hideHeader}
      title={match(viewState)
        .with('detail', () => 'MCP Client')
        .with('list', () => 'MCP')
        .with('loading', () => 'MCP')
        .with('error', () => 'Failed to load MCP Clients')
        .with('setup', () => MCP_SETUP_OPTION_LABELS[selectedSetup!])
        .exhaustive()}
      onBackButtonClick={
        viewState === 'detail' || viewState === 'setup' ? onCancel : undefined
      }
      topButtonSection={
        viewState === 'list' && !hideHeader ? (
          <Menu
            items={setupMenuItems}
            justify="justify-end"
            inverted
            dataTestId="mcp-add-setup-menu"
          >
            Add+
          </Menu>
        ) : null
      }
    >
      {match(viewState)
        .with('error', () => (
          <TableLoadError
            entityColumnName="Connections"
            rightColumnName="ID"
            {...{ error, retry: refresh, fetching }}
          />
        ))
        .with('loading', () => <LoadingText text="Loading" />)
        .with('list', () => (
          <Table
            textSize="md"
            data={mcpClients?.nodes ?? []}
            onRowClick={onRowClick}
            schema={schema}
            emptyValue={emptyValue}
            borderedRows
          />
        ))
        .with('detail', () => (
          <McpClientDetail
            mcpClient={selectedClient!}
            onConfirmedDelete={onConfirmedDelete}
          />
        ))
        .with('setup', () => <McpSetupInstructions setup={selectedSetup!} />)
        .exhaustive()}
    </SettingsModalLayout>
  );
};
